// React
import { useState } from 'react';

// Mantine
import {
  Alert,
  Flex,
  NumberFormatter,
  SegmentedControl,
  useMantineTheme,
} from '@mantine/core';
import { MRT_Row, MRT_TableOptions, MRT_ColumnDef } from 'mantine-react-table';

// Icons
import { IconInfoCircle } from '@tabler/icons-react';

// Columns
import { TransactionColumns } from 'columns/Transaction';

// Interfaces
import {
  Credit,
  Debit,
  Statement,
  Payor,
  Provider,
  Status,
  Tariff,
} from 'interfaces';

// API
import { useGetStatementData } from 'api/resource';

// Components
import BillingDataGrid from 'components/common/BillingDataGrid';
import { DebitDetailPanel } from 'pages/transactions/components/DebitDetailPanel';

import { useTutorialStore } from 'store/tutorialStore';
import {
  mockDebits,
  mockCredits,
} from 'test-fixtures/mocks/frontend';
import { formatDateOnly } from 'utils/dateUtils';

export interface StatementDetailPanelProps {
  row: MRT_Row<Statement>;
  payors: Payor[];
  providers: Provider[];
  statuses: Status[];
  tariffs: Tariff[];
}

export const StatementDetailPanel = ({
  row,
  payors,
  providers,
  statuses,
  tariffs,
}: StatementDetailPanelProps) => {
  const [showAlert, setShowAlert] = useState<boolean>(true);
  const [showSummary, setShowSummary] = useState<boolean>(true);
  const theme = useMantineTheme();
  const { isTutorialActive } = useTutorialStore();

  const statementPath = row.original.statementPath ?? '';
  const s3Path = encodeURIComponent(statementPath);
  const {
    data: statementData,
    isLoading,
    isError,
    error,
  } = useGetStatementData({ s3Path: isTutorialActive ? '' : s3Path });
  const debitColumns: MRT_ColumnDef<Debit>[] = TransactionColumns({
    payors,
  });

  if (isLoading) {
    return <div>Loading statement data...</div>;
  }

  if (isError) {
    return <div>Error loading statement data: {error?.message}</div>;
  }

  if (!isTutorialActive && !statementData) {
    return (
      <Alert icon={<IconInfoCircle />} color="yellow" variant="outline">
        Statement details are unavailable for this row.
      </Alert>
    );
  }

  const debitData = isTutorialActive
    ? mockDebits
    : statementData?.statementDebits ?? [];
  const netAmount = Number(statementData?.statementTotalAmount ?? 0);
  const creditData = isTutorialActive
    ? mockCredits.filter(
        (credit) => credit.providerID === row.original.providerID
      )
    : statementData?.statementCredits ?? [];

  const debitTableOptions: MRT_TableOptions<Debit> = {
    data: debitData,
    columns: debitColumns,
    enableEditing: false,
    enableBottomToolbar: false,
    enableTopToolbar: true,
    enableToolbarInternalActions: false,
    enableRowActions: false,
    enableBatchRowSelection: false,
    enableExpanding: false,
    enableClickToCopy: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableExpandAll: false,
    enablePagination: false,
    enableRowSelection: false,
    enableSorting: false,
    enableFilters: false,
    enableRowNumbers: false,
    state: {
      density: 'xs',
    },
    renderDetailPanel: ({ row }) => {
      return (
        <DebitDetailPanel
          row={row}
          providers={providers}
          statuses={statuses}
          tariffs={tariffs}
        />
      );
    },
  };

  const isNonOverheadCredit = (credit: Credit): boolean => {
    return (
      credit.description?.includes('deducted') ||
      credit.description?.includes('Interest')
    );
  };

  const creditTableOptions: MRT_TableOptions<Credit> = {
    data: creditData,
    columns: [
      {
        accessorKey: 'date',
        header: 'Date',
        accessorFn: (row) => {
          return formatDateOnly(row?.date);
        },
      },
      {
        accessorKey: 'amount',
        header: 'Net',
        accessorFn: (row) => {
          return isNonOverheadCredit(row)
            ? Number(row.amount).toFixed(2)
            : netAmount.toFixed(2);
        },
        Cell: ({ cell }) => {
          return (
            <NumberFormatter
              prefix="$ "
              value={cell.getValue() as number}
              thousandSeparator
            />
          );
        },
      },
      {
        header: 'Overhead',
        accessorFn: (row) => {
          return isNonOverheadCredit(row)
            ? Number('0.00').toFixed(2)
            : (Number(row.amount) - netAmount).toFixed(2);
        },
        Cell: ({ cell }) => {
          return (
            <NumberFormatter
              prefix="$ "
              value={cell.getValue() as number}
              thousandSeparator
            />
          );
        },
      },
      {
        header: 'Gross',
        accessorFn: (row) => {
          return Number(row.amount).toFixed(2);
        },
        Cell: ({ cell }) => {
          return (
            <NumberFormatter
              prefix="$ "
              value={cell.getValue() as number}
              thousandSeparator
            />
          );
        },
      },
      {
        accessorKey: 'description',
        header: 'Description',
        accessorFn: (row) => {
          return row?.description ? row?.description : 'Payment from insuror';
        },
      },
    ],
    enableEditing: false,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableToolbarInternalActions: false,
    enableRowActions: false,
    enableBatchRowSelection: false,
    enableExpanding: false,
    enableClickToCopy: false,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableExpandAll: false,
    enablePagination: false,
    enableRowSelection: false,
    enableSorting: false,
    enableFilters: false,
    enableRowNumbers: false,
    state: {
      showAlertBanner: showAlert,
      density: 'xs',
    },
    renderToolbarAlertBannerContent: () => {
      return (
        <Alert
          icon={<IconInfoCircle />}
          variant="outline"
          color="yellow"
          withCloseButton
          radius={'md'}
          w={'100%'}
          onClose={() => {
            setShowAlert(false);
          }}
          style={{
            textAlign: 'center',
            background: 'transparent',
          }}
        >
          NOTE: If you notice negative values please contact MB Health at
          204-786-7355 to find out why
        </Alert>
      );
    },
    mantineToolbarAlertBannerProps: () => {
      return {
        style: {
          background: 'transparent',
        },
      };
    },
  };

  return (
    <Flex
      gap="md"
      w="100%"
      direction={'column'}
      className="statement-detail-panel"
    >
      <Flex justify={'center'} gap={'lg'}>
        <SegmentedControl
          value={showSummary ? 'summary' : 'claims'}
          onChange={(value) => setShowSummary(value === 'summary')}
          data={[
            { label: 'Summary', value: 'summary' },
            // { label: 'Claims', value: 'claims' },
          ]}
          className="statement-detail-panel-segmented-control"
          size="md"
          color={theme.colors.blue[6]}
          radius="md"
          fullWidth
        />
      </Flex>

      {!showSummary ? (
        <BillingDataGrid data={debitTableOptions} />
      ) : (
        <BillingDataGrid data={creditTableOptions} />
      )}
    </Flex>
  );
};
